import { IMeta, IPagination, ISort } from "@delanobgt/admin-core/util/types";
import { produce } from "immer";
import { Reducer } from "redux";
import { EBankSortField, IBank, PBankFilter } from "src/core/bank/entities";
import {
  ActionType,
  createAction,
  createAsyncAction,
  getType,
} from "typesafe-actions";

/*
 * state
 */
export interface IBankState {
  banks: IBank[];
  loading: boolean;
  error: unknown;
  meta: IMeta;
  filter: PBankFilter;
  pagination: IPagination;
  sorts: ISort<EBankSortField>[];
  refreshDelay: number;
}

const INITIAL_STATE: IBankState = {
  banks: [],
  loading: false,
  error: null,
  meta: {
    limit: 10,
    offset: 0,
    total: 0,
  },
  filter: {},
  pagination: {
    limit: 10,
    offset: 0,
  },
  sorts: [],
  refreshDelay: 5000,
};

/*
 * action types
 */
const FETCH_REQUESTED = "bank/FETCH_REQUESTED";
const FETCH_SUCCEEDED = "bank/FETCH_SUCCEEDED";
const FETCH_FAILED = "bank/FETCH_FAILED";
const SET_FILTER_PAGINATION_SORT = "bank/SET_FILTER_PAGINATION_SORT";
const SET_REFRESH_DELAY = "bank/SET_REFRESH_DELAY";
const POLLING_START_REQUESTED = "bank/POLLING_START_REQUESTED";
const POLLING_STOP_REQUESTED = "bank/POLLING_STOP_REQUESTED";

/*
 * action creators
 */
export const actions = {
  setFilterPaginationSort: createAction(SET_FILTER_PAGINATION_SORT)<{
    filter: PBankFilter;
    pagination: IPagination;
    sorts: ISort<EBankSortField>[];
  }>(),

  setRefreshDelay: createAction(SET_REFRESH_DELAY)<{
    refreshDelay: number;
  }>(),

  fetchAsync: createAsyncAction(FETCH_REQUESTED, FETCH_SUCCEEDED, FETCH_FAILED)<
    unknown,
    {
      data: IBank[];
      meta: IMeta;
    },
    unknown
  >(),

  pollingStartRequested: createAction(POLLING_START_REQUESTED)(),

  pollingStopRequested: createAction(POLLING_STOP_REQUESTED)(),
};
export type TActions = ActionType<typeof actions>;

const reducer: Reducer<IBankState, TActions> = (
  state = INITIAL_STATE,
  action
) => {
  return produce<IBankState>(state, (draft) => {
    switch (action.type) {
      case getType(actions.setFilterPaginationSort): {
        const { payload } = action;
        draft.filter = payload.filter;
        draft.pagination = payload.pagination;
        draft.sorts = payload.sorts;
        break;
      }
      case getType(actions.setRefreshDelay): {
        const { payload } = action;
        draft.refreshDelay = payload.refreshDelay;
        break;
      }
      case getType(actions.fetchAsync.request): {
        draft.loading = true;
        draft.error = null;
        break;
      }
      case getType(actions.fetchAsync.success): {
        const {
          payload: { data, meta },
        } = action;
        draft.banks = data;
        draft.loading = false;
        draft.error = null;
        draft.meta = meta;
        break;
      }
      case getType(actions.fetchAsync.failure): {
        const { payload } = action;
        draft.loading = false;
        draft.error = payload;
        break;
      }
    }
  });
};

export default reducer;
